import { useEffect } from "react";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import useHttp from "../../hooks/use-http";
import { RootState } from "../../store";
import { useAppDispatch } from "../../store/hooks";
import { EventsActions } from "../../store/events-slice";
import { UIActions } from "../../store/ui-slice";
import { get2DigitsDate, getLongDate, getWeekday } from "../helpers/DateHelper";

import classes from "./FastAddEventForm.module.css";
import { t } from "../../i18n";

type FastAddFormValues = { title: string; important: boolean };

const fastAddSchema: yup.ObjectSchema<FastAddFormValues> = yup.object({
  title: yup.string().trim().required(),
  important: yup.boolean().required(),
});

const emptyFastAddValues: FastAddFormValues = { title: "", important: false };

const FastAddEventForm = () => {

  const dispatch = useAppDispatch();

  const { uid } = useSelector((state: RootState) => state.auth);
  const { userSelectedDate } = useSelector((state: RootState) => state.events);
  const { sendRequest: sendAddRequest } = useHttp();

  const outputSelectedDate = getLongDate(userSelectedDate);
  const outputSelectedDay = getWeekday(userSelectedDate);
  const outputShortSelectedDate = get2DigitsDate(userSelectedDate);

  const wrapperClass = `${classes.wrapper} ${
    window.desktop ? classes.wrapperDesktop : ""
  }`;

  const { register, handleSubmit, reset, setFocus, watch } = useForm<FastAddFormValues>({
    resolver: yupResolver(fastAddSchema),
    defaultValues: emptyFastAddValues,
  });

  // trim, as the schema does: spaces alone would not save.
  const canAdd = watch("title").trim().length > 0;

  useEffect(() => {
    setFocus("title");
  }, [setFocus]);

  const onFastAddHandler = (values: FastAddFormValues) => {
    const eventData = {
      date: userSelectedDate,
      title: values.title,
      timeFrom: "",
      timeTill: "",
      important: values.important,
      reminder: false,
      isBirthday: false,
      repeat: "never",
      description: "",
      deleted: false,
    };

    sendAddRequest(
      {
        dbPath: "/tasks/" + uid,
        method: "POST",
        body: eventData,
        headers: {
          "Content-Type": "application/json",
        },
        hideAfterRequest: false,
        successMessage: t.eventCreated,
      },
      (data: { name: string }) => {
        reset(emptyFastAddValues);
        dispatch(EventsActions.addEvent({ ...eventData, id: data.name }));
      }
    );
  };

  const invalidFastAddHandler = () => {
    dispatch(
      UIActions.error({
        message: t.enterEventTitle,
      })
    );
  };

  return (
    <div className={wrapperClass}>
      <div className={classes.today}>
        <div className={classes.date}>{outputSelectedDate}</div>
        <div className={classes.day}>{outputSelectedDay}</div>
      </div>

      {window.desktop && (
        <div className={classes.addWrapper}>
          <form onSubmit={handleSubmit(onFastAddHandler, invalidFastAddHandler)}>
            <div className={classes.addDate}>{outputShortSelectedDate}</div>
            {/* Before the title, as in the event form, and filled blue when on. */}
            <div className={classes.importantToggle}>
              <input
                type="checkbox"
                id="fast-add-important"
                className={classes.importantCheckbox}
                {...register("important")}
              />
              <label
                htmlFor="fast-add-important"
                className={classes.importantLabel}
                title={t.important}
              >
                <svg className="svg" viewBox="0 0 50 50">
                  <path
                    className={classes.starFill}
                    d="M25 38.62 9.55 50l6.01-18.82L0 19.1l19.3.19L25 0l5.7 19.29 19.3-.19-15.56 12.08 6.01 18.82Z"
                  />
                  <path
                    d="M7040,9511.62L7024.55,9523l6.01-18.82L7015,9492.1l19.3,0.19L7040,9473l5.7,19.29,19.3-.19-15.56,12.08,6.01,18.82Zm-14.41,8.13,13.22-9.74,1.19-.87,1.19,0.87,13.22,9.74,0.84,2.63-0.32.24-2.23-1.64-5.17-16.19-0.42-1.33,1.1-.86,13.49-10.47,2.63-.02,0.14,0.4-2.08,1.61-16.67.17-1.51.02-0.42-1.45-4.83-16.33,0.87-2.96h0.34l0.87,2.96-4.83,16.33-0.42,1.45-1.51-.02-16.67-.17-2.08-1.61,0.14-.4,2.63,0.02,13.49,10.47,1.1,0.86-0.42,1.33-5.17,16.19-2.23,1.64-0.32-.24Z"
                    transform="translate(-7015 -9473)"
                  />
                </svg>
              </label>
            </div>
            <input
              maxLength={256}
              type="text"
              inputMode="text"
              className={classes['fast-add-input']}
              autoComplete="off"
              placeholder={t.quickAdd}
              {...register("title")}
            />
            <button className={classes.simpleButton} disabled={!canAdd}>
              {t.add}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default FastAddEventForm;
